import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1758691031958-29541f3207a5?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";

export default function Articles() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/articles/public")
      .then(({ data }) => setArticles(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <Seo
        title="Senior care articles & guides for families"
        description="Real, honest guides on assisted living, memory care, independent living and the conversations every family is facing. Written for families, not marketers."
        path="/articles"
        type="website"
        keywords="senior care articles, assisted living guide, memory care guide, aging parents, senior care advice"
        jsonLd={
          articles.length
            ? {
                "@context": "https://schema.org",
                "@type": "CollectionPage",
                "name": "CareHomeConnect Articles",
                "url": "https://carehomeconnect.org/articles",
                "description": "Senior care articles and guides for families.",
                "hasPart": articles.slice(0, 10).map((a) => ({
                  "@type": "Article",
                  "headline": a.title,
                  "url": `https://carehomeconnect.org/articles/${a.slug}`,
                  "image": a.cover_image,
                })),
              }
            : null
        }
      />
      <PublicNav />
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-10">
        <div className="text-sm text-[#9CAF88] font-semibold mb-3 uppercase tracking-wider">The library</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1A2E24] tracking-tight max-w-3xl">
          Real guidance for real families
        </h1>
        <p className="mt-5 text-lg text-[#5C6B62] max-w-2xl leading-relaxed">
          Honest, easy-to-read stories and guides — written for families, not marketers.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20">
        {loading ? (
          <div className="text-[#5C6B62]" data-testid="articles-loading">Loading…</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="articles-grid">
            {articles.map((a) => (
              <Link
                to={`/articles/${a.slug}`}
                key={a.id}
                data-testid={`article-card-${a.slug}`}
                className="card-soft p-0 overflow-hidden group"
              >
                <div className="aspect-[4/3] overflow-hidden bg-[#E2DFD8]">
                  <img
                    src={a.cover_image}
                    alt={a.title}
                    onError={(e) => { if (e.currentTarget.src !== FALLBACK_IMAGE) e.currentTarget.src = FALLBACK_IMAGE; }}
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="text-xs font-medium text-[#9CAF88] uppercase tracking-wider mb-2">{a.category}</div>
                  <h3 className="font-display text-lg font-semibold text-[#1A2E24] leading-snug mb-2 group-hover:text-[#2C4C3B]">
                    {a.title}
                  </h3>
                  <p className="text-sm text-[#5C6B62] line-clamp-2">{a.hook}</p>
                  <div className="mt-4 inline-flex items-center gap-1.5 text-[#2C4C3B] font-medium text-sm">
                    Read <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
      <PublicFooter />
    </div>
  );
}
